import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  SettingsUsers,
  SettingsCarriers,
  SettingsCompany,
  SettingsPreferences,
  SettingsData,
  SettingsQuickBooks,
  SettingsAuditTrail
} from '../components/settings';
import '../styles/Settings.css';

const Settings = () => {
  const { user, isAdmin, isStrictAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    return user?.role === 'admin' ? 'users' : 'carriers';
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // User state
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userModalMode, setUserModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', role: 'staff' });
  const [userSearch, setUserSearch] = useState('');
  const [userViewMode, setUserViewMode] = useState(() => {
    const saved = localStorage.getItem('usersViewMode');
    if (saved) return saved;
    return window.innerWidth <= 1024 ? 'cards' : 'table';
  });

  // Carrier state
  const [carriers, setCarriers] = useState([]);
  const [showCarrierModal, setShowCarrierModal] = useState(false);
  const [carrierModalMode, setCarrierModalMode] = useState('add');
  const [editingCarrier, setEditingCarrier] = useState(null);
  const [carrierForm, setCarrierForm] = useState({ name: '' });
  const [carrierSearch, setCarrierSearch] = useState('');

  // Company & preferences state
  const [companyInfo, setCompanyInfo] = useState({ name: 'Montreal Kosher Bakery', address: '', phone: '' });
  const [preferences, setPreferences] = useState({ weightUnit: 'kg', dateFormat: 'MM/DD/YYYY' });
  const [dbStats, setDbStats] = useState({ totalItems: 0, totalPallets: 0, totalShipments: 0 });

  // QuickBooks state
  const [qbSettings, setQbSettings] = useState({
    qb_enabled: 'false', qb_wc_password: '', qb_auto_invoice: 'true', qb_default_tax_code: 'E', qb_last_sync: ''
  });
  const [qbTaxCodes, setQbTaxCodes] = useState([]);
  const [qbQueueStats, setQbQueueStats] = useState({ pending: 0, completed: 0, failed: 0 });
  const [qbSyncing, setQbSyncing] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      window.location.href = '/dashboard';
      return;
    }
    loadUsers();
    loadCarriers();
    loadCompanyInfo();
    loadPreferences();
    loadDbStats();
    loadQbSettings();
  }, []);

  // Load functions
  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data || []);
    } catch (err) {
      showMessage('error', 'Failed to load users');
      setUsers([]);
    }
  };

  const loadCarriers = async () => {
    try {
      const response = await api.get('/carriers');
      setCarriers(response.data || []);
    } catch (err) {
      showMessage('error', 'Failed to load carriers');
      setCarriers([]);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await api.get('/settings/company');
      if (response.data) setCompanyInfo(response.data);
    } catch (err) { /* Using default */ }
  };

  const loadPreferences = async () => {
    try {
      const response = await api.get('/settings/preferences');
      if (response.data) setPreferences(response.data);
    } catch (err) { /* Using default */ }
  };

  const loadDbStats = async () => {
    try {
      const response = await api.get('/settings/stats');
      if (response.data) setDbStats(response.data);
    } catch (err) { /* Failed to load */ }
  };

  const loadQbSettings = async () => {
    try {
      const [settingsRes, taxCodesRes, statsRes] = await Promise.all([
        api.get('/qb/settings').catch(() => ({ data: {} })),
        api.get('/qb/tax-codes').catch(() => ({ data: [] })),
        api.get('/qb/queue/stats').catch(() => ({ data: { pending: 0, completed: 0, failed: 0 } }))
      ]);
      if (settingsRes.data) setQbSettings(prev => ({ ...prev, ...settingsRes.data }));
      setQbTaxCodes(taxCodesRes.data || []);
      setQbQueueStats(statsRes.data || { pending: 0, completed: 0, failed: 0 });
    } catch (err) { /* QB not configured */ }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // User handlers
  const handleAddUser = () => {
    setEditingUser(null);
    setSelectedUser(null);
    setUserModalMode('add');
    setUserForm({ username: '', email: '', password: '', role: 'staff' });
    setShowUserModal(true);
  };

  const handleViewUser = (userToView) => {
    setSelectedUser(userToView);
    setUserModalMode('view');
    setShowUserModal(true);
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setSelectedUser(userToEdit);
    setUserModalMode('edit');
    setUserForm({ username: userToEdit.username, email: userToEdit.email, password: '', role: userToEdit.role });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      showMessage('success', 'User deleted successfully');
      await loadUsers();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, userForm);
        showMessage('success', 'User updated successfully');
        const updatedUser = { ...editingUser, ...userForm };
        setSelectedUser(updatedUser);
        setUserModalMode('view');
      } else {
        const response = await api.post('/users', userForm);
        showMessage('success', 'User created successfully');
        setSelectedUser(response.data);
        setUserModalMode('view');
      }
      setUserForm({ username: '', email: '', password: '', role: 'staff' });
      await loadUsers();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserViewMode = (mode) => {
    setUserViewMode(mode);
    localStorage.setItem('usersViewMode', mode);
  };

  // Carrier handlers
  const handleAddCarrier = () => {
    setEditingCarrier(null);
    setCarrierModalMode('add');
    setCarrierForm({ name: '' });
    setShowCarrierModal(true);
  };

  const handleEditCarrier = (carrier) => {
    setEditingCarrier(carrier);
    setCarrierModalMode('edit');
    setCarrierForm({ name: carrier.name });
    setShowCarrierModal(true);
  };

  const handleSaveCarrier = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingCarrier) {
        await api.put(`/carriers/${editingCarrier.id}`, carrierForm);
        showMessage('success', 'Carrier updated successfully');
      } else {
        await api.post('/carriers', carrierForm);
        showMessage('success', 'Carrier created successfully');
      }
      setCarrierForm({ name: '' });
      setShowCarrierModal(false);
      await loadCarriers();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save carrier');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCarrier = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carrier?')) return;
    try {
      await api.delete(`/carriers/${id}`);
      showMessage('success', 'Carrier deleted successfully');
      await loadCarriers();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to delete carrier');
    }
  };

  // Company/preferences handlers
  const handleSaveCompanyInfo = async (info) => {
    setLoading(true);
    try {
      await api.put('/settings/company', info);
      showMessage('success', 'Company information updated successfully');
    } catch (err) {
      showMessage('error', 'Failed to update company information');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async (prefs) => {
    setLoading(true);
    try {
      await api.put('/settings/preferences', prefs);
      showMessage('success', 'Preferences updated successfully');
    } catch (err) {
      showMessage('error', 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  // Data export
  const handleExportData = async () => {
    setLoading(true);
    try {
      const [itemsRes, palletsRes, shipmentsRes] = await Promise.all([
        api.get('/items'), api.get('/pallets'), api.get('/shipments')
      ]);
      const exportData = {
        items: itemsRes.data || [], pallets: palletsRes.data || [],
        shipments: shipmentsRes.data || [], exportedAt: new Date().toISOString()
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `mk_backup_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      showMessage('success', 'Data exported successfully');
    } catch (err) {
      showMessage('error', 'Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // QB handlers
  const handleSaveQbSettings = async (settings) => {
    setLoading(true);
    try {
      await api.put('/qb/settings', settings);
      showMessage('success', 'QuickBooks settings saved');
      await loadQbSettings();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save QB settings');
    } finally {
      setLoading(false);
    }
  };

  const handleQbSync = async () => {
    setQbSyncing(true);
    try {
      await api.post('/qb/queue/sync/full');
      showMessage('success', 'Sync queued. Connect Web Connector to complete.');
      await loadQbSettings();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to queue sync');
    } finally {
      setQbSyncing(false);
    }
  };

  const handleDownloadQwc = async () => {
    try {
      const response = await api.get('/qb/download-qwc', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/xml' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'MontrealKosher.qwc';
      link.click();
    } catch (err) {
      showMessage('error', 'Failed to download QWC file');
    }
  };

  const handleRefreshQbStatus = async () => {
    setLoading(true);
    try {
      await loadQbSettings();
      showMessage('success', 'Status refreshed');
    } catch (err) {
      showMessage('error', 'Failed to refresh status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div className="header-top">
          <h1>Settings</h1>
          {activeTab === 'users' && (
            <button className="add-button" onClick={handleAddUser}>+ Add User</button>
          )}
          {activeTab === 'carriers' && (
            <button className="add-button" onClick={handleAddCarrier}>+ Add Carrier</button>
          )}
        </div>
      </div>

      {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

      <div className="settings-tabs">
        {isStrictAdmin() && (
          <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
            👥 Users
          </button>
        )}
        <button className={`tab-btn ${activeTab === 'carriers' ? 'active' : ''}`} onClick={() => setActiveTab('carriers')}>
          🚚 Carriers
        </button>
        <button className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`} onClick={() => setActiveTab('company')}>
          🏢 Company
        </button>
        <button className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`} onClick={() => setActiveTab('preferences')}>
          ⚙️ Prefs
        </button>
        <button className={`tab-btn ${activeTab === 'data' ? 'active' : ''}`} onClick={() => setActiveTab('data')}>
          💾 Data
        </button>
        <button className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
          Audit Trail
        </button>
        {/* QB tab hidden until paid - feature flag */}
      </div>

      <div className="settings-content">
        {activeTab === 'users' && isStrictAdmin() && (
          <SettingsUsers
            users={users}
            userSearch={userSearch}
            onUserSearchChange={setUserSearch}
            userViewMode={userViewMode}
            onToggleUserViewMode={toggleUserViewMode}
            onViewUser={handleViewUser}
            onEditUser={handleEditUser}
          />
        )}
        {activeTab === 'carriers' && (
          <SettingsCarriers
            carriers={carriers}
            carrierSearch={carrierSearch}
            onCarrierSearchChange={setCarrierSearch}
            onEditCarrier={handleEditCarrier}
            onDeleteCarrier={handleDeleteCarrier}
          />
        )}
        {activeTab === 'company' && (
          <SettingsCompany
            companyInfo={companyInfo}
            setCompanyInfo={setCompanyInfo}
            onSave={handleSaveCompanyInfo}
            loading={loading}
          />
        )}
        {activeTab === 'preferences' && (
          <SettingsPreferences
            preferences={preferences}
            setPreferences={setPreferences}
            onSave={handleSavePreferences}
            loading={loading}
          />
        )}
        {activeTab === 'data' && (
          <SettingsData
            dbStats={dbStats}
            onExportData={handleExportData}
            loading={loading}
          />
        )}
        {activeTab === 'audit' && (
          <SettingsAuditTrail />
        )}
        {activeTab === 'quickbooks' && (
          <SettingsQuickBooks
            qbSettings={qbSettings}
            setQbSettings={setQbSettings}
            qbTaxCodes={qbTaxCodes}
            qbQueueStats={qbQueueStats}
            onSaveSettings={handleSaveQbSettings}
            onSync={handleQbSync}
            onDownloadQwc={handleDownloadQwc}
            onRefreshStatus={handleRefreshQbStatus}
            loading={loading}
            syncing={qbSyncing}
          />
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {userModalMode === 'view' && 'User Details'}
                {userModalMode === 'add' && 'Add New User'}
                {userModalMode === 'edit' && 'Edit User'}
              </h2>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>×</button>
            </div>
            {userModalMode === 'view' ? (
              <div className="modal-body">
                <div className="view-grid">
                  <div className="view-item"><strong>Username:</strong><span>{selectedUser?.username}</span></div>
                  <div className="view-item"><strong>Email:</strong><span>{selectedUser?.email || '-'}</span></div>
                  <div className="view-item"><strong>Role:</strong><span className={`role-badge ${selectedUser?.role}`}>{selectedUser?.role}</span></div>
                </div>
                <div className="modal-actions">
                  {selectedUser?.id !== user?.id && (
                    <button type="button" className="btn-danger" onClick={() => { handleDeleteUser(selectedUser.id); setShowUserModal(false); }}>
                      Delete
                    </button>
                  )}
                  <button type="button" className="btn-primary" onClick={() => handleEditUser(selectedUser)}>Edit</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSaveUser} className="modal-form">
                <div className="form-group">
                  <label>Username *</label>
                  <input type="text" value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Password {editingUser ? '(leave blank to keep)' : '*'}</label>
                  <input type="password" autoComplete="new-password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required={!editingUser} />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} required>
                    <option value="staff">Staff</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowUserModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save User'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Carrier Modal */}
      {showCarrierModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{carrierModalMode === 'add' ? 'Add New Carrier' : 'Edit Carrier'}</h2>
              <button className="modal-close" onClick={() => setShowCarrierModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveCarrier} className="modal-form">
              <div className="form-group">
                <label>Carrier Name *</label>
                <input type="text" value={carrierForm.name} onChange={(e) => setCarrierForm({ ...carrierForm, name: e.target.value })} placeholder="Enter carrier name" required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCarrierModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Carrier'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
